import { useEffect } from 'react';

interface PageTitleOptions {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

// Light-weight client-side metadata updater for FAME programmatic pages.
// Mirrors the FDME hook so both sites stay in sync.
export function usePageTitle({ title, description, keywords, canonical, schema }: PageTitleOptions) {
  useEffect(() => {
    document.title = title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', description);

    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', keywords);
    }

    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonical);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', description);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && canonical) ogUrl.setAttribute('content', canonical);

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', description);

    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterUrl && canonical) twitterUrl.setAttribute('content', canonical);

    // Remove schema scripts added previously by this hook.
    const existingSchemas = document.querySelectorAll('script[data-schema-hook="true"]');
    existingSchemas.forEach((script) => script.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((schemaData) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema-hook', 'true');
        script.textContent = JSON.stringify(schemaData);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, canonical, schema]);
}
